import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import NavItem from '@plone/volto/components/theme/Navigation/NavItem';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import useLook from '../Look/useLook';
import { isActiveUrl, isTabActive, menuFor, menuItems } from './menu';

// Desktop menu: plain tabs, and tabs with a dropdown. A dropdown tab's label
// is still a link (it goes to the section's page); the arrow beside it opens
// the list, as does hovering. Escape, a click elsewhere, or leaving the tab
// closes it. The phone drawer is VLT's own and reads the same menu. The
// classic look's tabs have no dropdowns, so they render as plain tabs.
const Tab = ({ item, index, pathname, open, setOpen }) => {
  const id = `asaj-submenu-${index}`;

  return (
    <li
      className={cx('has-submenu', { open })}
      onMouseEnter={() => setOpen(index)}
      onMouseLeave={() => setOpen(null)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(null);
      }}
    >
      <UniversalLink
        href={item.url || '/'}
        className={cx('item', { active: isTabActive(item, pathname) })}
        onClick={() => setOpen(null)}
      >
        {item.title}
      </UniversalLink>
      <button
        type="button"
        className="submenu-toggle"
        aria-expanded={open}
        aria-controls={id}
        aria-label={`${item.title} menu`}
        onClick={() => setOpen(open ? null : index)}
      />
      <ul id={id} className="asaj-submenu" hidden={!open}>
        {item.items.map((child) => (
          <li key={child.url}>
            <UniversalLink
              href={child.url}
              className={cx({ active: isActiveUrl(child.url, pathname) })}
              onClick={() => setOpen(null)}
            >
              {child.title}
            </UniversalLink>
          </li>
        ))}
      </ul>
    </li>
  );
};

const Navigation = ({ pathname }) => {
  const look = useLook();
  // From the look, not the store: right on the server's first paint too
  const items = useMemo(() => menuItems(menuFor(look)), [look]);
  const lang = useSelector((state) => state.intl.locale);
  const [open, setOpen] = useState(null);
  const nav = useRef(null);

  useEffect(() => setOpen(null), [pathname]);

  useEffect(() => {
    const onKey = (event) => event.key === 'Escape' && setOpen(null);
    const onPress = (event) => {
      if (nav.current && !nav.current.contains(event.target)) setOpen(null);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPress);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPress);
    };
  }, []);

  return (
    <nav
      id="navigation"
      aria-label="navigation"
      className="navigation"
      ref={nav}
    >
      <div className="computer large screen widescreen only">
        <ul className="desktop-menu">
          {items.map((item, index) =>
            item.items?.length ? (
              <Tab
                key={item.url}
                item={item}
                index={index}
                pathname={pathname}
                open={open === index}
                setOpen={setOpen}
              />
            ) : (
              <li key={item.url}>
                <NavItem item={item} lang={lang} />
              </li>
            ),
          )}
        </ul>
      </div>
    </nav>
  );
};

export default Navigation;
